/**
 * In-memory embed session for the Contract Checker iframe (embedded inside
 * SELISE Signature).
 *
 * Security contract (see the integration plan):
 *   - The access token lives ONLY in module memory. It is NEVER written to
 *     localStorage / sessionStorage / IndexedDB / cookies, so it disappears
 *     when the tab is closed or refreshed. We deliberately do NOT use the
 *     persisted zustand `auth-storage` store — that is shared across same-
 *     origin tabs and would clobber a real Legal Templates session.
 *   - The token resolution layer (https.ts, blocks-ai-agent.ts) prefers this
 *     embed token when a session is active, so all existing Contract Checker
 *     API calls authenticate as the embedded user without further changes.
 */

export interface EmbedUser {
  id: string;
  email?: string;
  name?: string;
}

export interface EmbedOrganization {
  /** UDS/Blocks organization id used to scope OrgContractAnalysis rows. */
  id: string;
  name?: string;
  /** SELISE Signature (ARC) OrganizationId, when the backend provides it. */
  sigOrgId?: string;
}

export interface EmbedSession {
  accessToken: string;
  user: EmbedUser;
  organization: EmbedOrganization;
  /** Optional SELISE Signature document id to auto-load and analyse. */
  documentId?: string;
}

let session: EmbedSession | null = null;
// The signed PDF text extracted at bootstrap (when the session carried a
// document), held in memory so the "new" route can prefill it.
let pendingDocument: { text: string; title: string } | null = null;

export const setEmbedSession = (next: EmbedSession): void => {
  session = next;
};

export const clearEmbedSession = (): void => {
  session = null;
  pendingDocument = null;
};

export const getEmbedSession = (): EmbedSession | null => session;

export const isEmbedSessionActive = (): boolean => session !== null;

export const getEmbedAccessToken = (): string | null => session?.accessToken ?? null;

export const getEmbedOrganizationId = (): string | null => session?.organization?.id ?? null;

// The signature-org id used to scope embed analyses. Prefer an explicit
// `sigOrgId` from the backend; fall back to the org id (the embed runs in the
// Signature org context, so the bootstrap org id is the signature org).
export const getEmbedSigOrgId = (): string | null =>
  session?.organization?.sigOrgId ?? session?.organization?.id ?? null;

export const setEmbedDocument = (doc: { text: string; title: string } | null): void => {
  pendingDocument = doc;
};

export const getEmbedDocument = (): { text: string; title: string } | null => pendingDocument;

export const takeEmbedDocument = (): { text: string; title: string } | null => {
  const doc = pendingDocument;
  pendingDocument = null;
  return doc;
};
